import React from 'react'
import PropTypes from 'prop-types'
import { FormTextField, useZionFormContext } from '@fs/zion-form'
import { Row, Cell, LabelSpacer } from '@fs/zion-ui'
import { useSlsStateContext, useSlsHighlightsContext } from '@fs/zion-sls'
import HighlightButton from '../../panels/shared/HighlightButton'
import { prepareFieldForHighlights, handleInputBlur, handleInputFocus } from '../../shared/utils/highlightUtils'

/**
 * OtherEventFields
 *
 * Renders a single "other" event field with a text input and a highlight button.
 * The component wires into Zion form and highlights contexts to synchronize
 * form values and highlight state.
 *
 * Props
 * @param {Object} props
 * @param {Object} props.otherField - The metadata for the other field to render. Must include `displayLabel`.
 * @param {boolean} [props.primaryEvent=false] - Whether this field belongs to the primary event or an additional event.
 * @param {number} props.eventIndex - Index of the event in the events array.
 * @param {number} props.itemIndex - Index of the item in the other[] array for the event.
 *
 * @returns {JSX.Element} The rendered other event field row.
 */
const OtherEventFields = ({ otherField, primaryEvent = false, eventIndex, itemIndex, splitScreenImageArk }) => {
  const { getValues } = useZionFormContext()
  const { slsTypes, images } = useSlsStateContext()
  const h = useSlsHighlightsContext()
  const basePath = `events.${primaryEvent ? 'primary' : 'additional'}[${eventIndex}].other[${itemIndex}]`

  return (
    <Row gutters="xxs">
      <FormTextField
        name={`${basePath}.value`}
        label={otherField.displayLabel}
        data-testid={`other-event-field-${itemIndex}`}
        onFocus={() => handleInputFocus(`${basePath}.id`, getValues, h)}
        onBlur={() => handleInputBlur(h)}
        growX
        dense
      />
      <Cell>
        <LabelSpacer />
        <HighlightButton
          field={prepareFieldForHighlights(getValues, otherField.type, basePath, slsTypes, images)}
          splitScreenImageArk={splitScreenImageArk}
          buttonProps={{ 'data-testid': `other-event-field-${itemIndex}-highlight-button` }}
          linkName={`essential-information-other-event-field-${itemIndex}-highlight`}
          growX
          iconOnly
        />
      </Cell>
    </Row>
  )
}
export default OtherEventFields

OtherEventFields.propTypes = {
  // Field metadata used to label the input. displayLabel is required.
  otherField: PropTypes.shape({
    displayLabel: PropTypes.string.isRequired,
    fieldType: PropTypes.string,
  }).isRequired,
  // If true, the field is part of the primary event, otherwise an additional event.
  primaryEvent: PropTypes.bool,
  // Index of the event in the events array
  eventIndex: PropTypes.number.isRequired,
  // Index of this item in the event's other[] array
  itemIndex: PropTypes.number.isRequired,
}

OtherEventFields.defaultProps = {
  primaryEvent: false,
}
